import React, { useMemo, useState } from "react";

const SECTION_META = [
  { key: "ddos", title: "DDoS Design" },
  { key: "alteon", title: "Alteon Design" },
  { key: "appsec", title: "Application Security Design" },
  { key: "management", title: "Management Design" },
  { key: "support", title: "Support Design" },
  { key: "training", title: "Training Design" },
  { key: "implementation", title: "Implementation Design" },
  { key: "commercial_administrative", title: "Commercial / Administrative Design" },
];

const APPSEC_APPLIANCE_FIELDS = new Set([
  "on_prem_deployment_mode",
  "interfaces_and_type",
  "ram",
  "disk",
  "power_supply",
  "cps",
  "pps",
  "concurrent_connections",
]);

const STATUSES = ["found", "not_found", "ambiguous", "not_applicable"];

function labelize(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function isDesignField(node) {
  return node && typeof node === "object" && "status" in node && "value_type" in node;
}

function displayValue(field) {
  if (!field) return "";
  if (field.value_type === "number" && field.number_value != null) {
    return String(field.number_value);
  }
  if (field.value_type === "boolean" && typeof field.boolean_value === "boolean") {
    return field.boolean_value ? "true" : "false";
  }
  if (field.value_type === "list") return field.list_json || field.text_value || "";
  if (field.value != null && typeof field.value !== "object") return String(field.value);
  return field.text_value || "";
}

function FieldEditor({ path, field, onSave, saving }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    text_value: displayValue(field),
    unit: field.unit || "",
    status: field.status || "not_found",
    pages: (field.pages || []).join(", "),
    evidence_summary: field.evidence_summary || "",
  });

  const start = () => {
    setDraft({
      text_value: displayValue(field),
      unit: field.unit || "",
      status: field.status || "not_found",
      pages: (field.pages || []).join(", "),
      evidence_summary: field.evidence_summary || "",
    });
    setOpen(true);
  };

  const parsePages = (value) =>
    String(value || "")
      .split(/[,\s]+/)
      .map((part) => Number(part))
      .filter((n) => Number.isFinite(n) && n >= 1);

  const payload = () => {
    const update = {
      path,
      unit: draft.unit,
      status: draft.status,
      pages: parsePages(draft.pages),
      evidence_summary: draft.evidence_summary,
    };
    if (field.value_type === "number") {
      const number = Number(draft.text_value);
      update.value_type = "number";
      update.number_value = Number.isFinite(number) ? number : null;
      update.text_value = draft.text_value;
    } else if (field.value_type === "boolean") {
      update.value_type = "boolean";
      update.boolean_value = String(draft.text_value).toLowerCase() === "true";
      update.text_value = draft.text_value;
    } else if (field.value_type === "list") {
      update.value_type = "list";
      update.list_json = draft.text_value;
      update.text_value = draft.text_value;
    } else {
      update.value_type = "text";
      update.text_value = draft.text_value;
    }
    return update;
  };

  return (
    <div className="rounded border border-slate-200 px-2 py-1.5 dark:border-slate-700" data-testid={`rfp-design-field-${path}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
            {labelize(path.split(".").slice(-1)[0])}
            {field.source === "engineer" ? (
              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                Engineer
              </span>
            ) : (
              <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                Model
              </span>
            )}
          </p>
          <p className="text-sm text-slate-700 dark:text-slate-200">
            {displayValue(field) || "—"}
            {field.unit ? ` ${field.unit}` : ""}
            <span className="ml-2 text-xs uppercase text-slate-400">{field.status}</span>
          </p>
          <p className="text-xs text-slate-500">
            Pages {(field.pages || []).join(", ") || "—"}
            {field.evidence_summary ? ` · ${field.evidence_summary}` : ""}
          </p>
        </div>
        <button type="button" className="text-xs text-emerald-700 hover:underline dark:text-emerald-300" onClick={start}>
          Edit
        </button>
      </div>
      {open ? (
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <input
            className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950"
            value={draft.text_value}
            onChange={(e) => setDraft((prev) => ({ ...prev, text_value: e.target.value }))}
            placeholder="Value"
          />
          <input
            className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950"
            value={draft.unit}
            onChange={(e) => setDraft((prev) => ({ ...prev, unit: e.target.value }))}
            placeholder="Unit"
          />
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950"
            value={draft.status}
            onChange={(e) => setDraft((prev) => ({ ...prev, status: e.target.value }))}
          >
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          <input
            className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950"
            value={draft.pages}
            onChange={(e) => setDraft((prev) => ({ ...prev, pages: e.target.value }))}
            placeholder="Pages"
          />
          <input
            className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950 sm:col-span-2"
            value={draft.evidence_summary}
            onChange={(e) => setDraft((prev) => ({ ...prev, evidence_summary: e.target.value }))}
            placeholder="Evidence"
          />
          <div className="flex gap-2 sm:col-span-2">
            <button
              type="button"
              disabled={saving || !onSave}
              className="rounded-lg border border-emerald-400 bg-emerald-50 px-3 py-1 text-sm disabled:opacity-60 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100"
              onClick={() => {
                if (onSave) onSave(payload());
                setOpen(false);
              }}
            >
              Save design edit
            </button>
            <button type="button" className="text-sm text-slate-500" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FieldGroup({ prefix, node, onSave, saving, hideKeys }) {
  if (!node || typeof node !== "object") return null;
  const entries = Object.entries(node).filter(([key]) => key !== "_meta" && key !== "items_requiring_human_validation");
  return (
    <div className="space-y-2">
      {entries.map(([key, value]) => {
        if (hideKeys?.has(key)) return null;
        const path = prefix ? `${prefix}.${key}` : key;
        if (isDesignField(value)) {
          return <FieldEditor key={path} path={path} field={value} onSave={onSave} saving={saving} />;
        }
        if (value && typeof value === "object") {
          return (
            <details key={path} className="rounded border border-slate-200 px-2 py-1 dark:border-slate-700" open>
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-500">
                {labelize(key)}
              </summary>
              <div className="mt-2">
                <FieldGroup prefix={path} node={value} onSave={onSave} saving={saving} />
              </div>
            </details>
          );
        }
        return null;
      })}
    </div>
  );
}

export default function RfpScopeDesignPanel({ design, onSaveDesign, saving }) {
  const visibleSections = useMemo(() => SECTION_META.filter((section) => design?.[section.key]), [design]);
  const appsecDeployment = String(design?.appsec?.deployment_model?.text_value || design?.appsec?.deployment_model?.value || "")
    .toLowerCase()
    .replace("-", "_");
  const hideAppsecAppliance = appsecDeployment === "cloud";

  if (!design || !visibleSections.length) return null;

  return (
    <section className="rounded-xl border border-sky-200 bg-sky-50/40 p-4 dark:border-sky-900 dark:bg-sky-950/20" data-testid="rfp-scope-design">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-sky-900 dark:text-sky-200">Design</h3>
      <p className="mt-1 text-xs text-sky-800/80 dark:text-sky-200/80">
        Structured sizing and service values extracted from approved Scope pages. Correct before Analysis uses them.
      </p>
      <div className="mt-3 space-y-2">
        {visibleSections.map((section) => (
          <details
            key={section.key}
            className="rounded-lg border border-sky-200 bg-white px-3 py-2 dark:border-sky-900 dark:bg-slate-900"
            data-testid={`rfp-scope-design-${section.key}`}
          >
            <summary className="cursor-pointer text-sm font-semibold text-slate-800 dark:text-slate-100">{section.title}</summary>
            <div className="mt-2">
              <FieldGroup
                prefix={section.key}
                node={design[section.key]}
                onSave={onSaveDesign}
                saving={saving}
                hideKeys={section.key === "appsec" && hideAppsecAppliance ? APPSEC_APPLIANCE_FIELDS : undefined}
              />
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}
